import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { Donut } from '../Donut';
import cc from 'classcat';
import { UserOrganizationPolicyDto } from '../../commonTypes/acs/user.types';
import { DateTime } from 'luxon';
import { useTranslation } from '../../hooks/useTranslation';

type DiagramProps = {
    policy: UserOrganizationPolicyDto;
    annualColor: string;
    usedColor: string;
    carriedOverColor?: string;
    setIsTermsOpen: (description: string) => void;
    hideInfo?: boolean;
    noMargin?: boolean;
    cursorStyle?: 'pointer' | 'default';
    scaled?: boolean;
    onClick?: (e: React.MouseEvent<HTMLElement>) => void;
};
export const Diagram = ({
    policy,
    annualColor,
    usedColor,
    carriedOverColor,
    setIsTermsOpen,
    hideInfo,
    noMargin,
    cursorStyle,
    scaled,
    onClick,
}: DiagramProps) => {
    const content = useSelector((state: RootState) => state.language.data);

    const carriedOverLimitPassed =
        DateTime.now() > DateTime.fromObject({ year: DateTime.now().year, month: policy.carriedOverValidToMonth, day: policy.carriedOverValidToDay });

    return (
        <div className={cc(['diagram', noMargin && 'diagram__no-margin'])} onClick={onClick}>
            <div className="diagram__wrapper">
                <div className={cc(['diagram__header', policy.isCarriedOver && !carriedOverLimitPassed && 'diagram__header-carried-over'])}>
                    <div className="diagram__legend">
                        <div>
                            <div className="diagram__legend--rect" style={{ backgroundColor: annualColor }} />
                            <span className="diagram__legend--text">{content.leaveRequest.availableDays}</span>
                        </div>
                        {policy.isCarriedOver && !carriedOverLimitPassed && (
                            <div>
                                <div className="diagram__legend--rect" style={{ backgroundColor: carriedOverColor }} />
                                <span className="diagram__legend--text">Carried Over</span>
                            </div>
                        )}
                        <div>
                            <div className="diagram__legend--rect" style={{ backgroundColor: usedColor }} />
                            <span className="diagram__legend--text">{content.leaveRequest.usedDays}</span>
                        </div>
                    </div>
                    {!hideInfo && (
                        <button className="diagram__icon" onClick={() => setIsTermsOpen(policy.description ?? '')}>
                            <img src="/svg/slider/info-icon.svg" alt="Policy description" />
                        </button>
                    )}
                </div>
                <Donut
                    policy={policy}
                    annualColor={annualColor}
                    usedColor={usedColor}
                    carriedOverColor={carriedOverColor}
                    cursorStyle={cursorStyle}
                    scaled={scaled}
                />
                <DiagramLabels policy={policy} carriedOverLimitPassed={carriedOverLimitPassed} />
            </div>
        </div>
    );
};

interface IDiagramLabels {
    policy: UserOrganizationPolicyDto;
    carriedOverLimitPassed: boolean;
}

interface ILabel {
    amount: number;
    label: string;
}

const Label = ({ amount, label }: ILabel) => {
    return (
        <>
            <p className={cc(['paragraph-3', 'diagram__value'])}>
                <span className="diagram__days">{amount}</span>
                {` ${label}`}
            </p>
            <div className="diagram__separator" />
        </>
    );
};

const DiagramLabels = ({ policy, carriedOverLimitPassed }: IDiagramLabels) => {
    const { t } = useTranslation('leaveRequest');

    const usedAmountValue = policy.isCarriedOver
        ? policy.initialAmount + policy.initialBonusDays - policy.amount + policy.initialCarriedOverAmount - policy.carriedOverAmount
        : policy.initialAmount + policy.initialBonusDays - policy.amount;

    return (
        <div className="diagram__values">
            {policy.isCarriedOver ? (
                <>
                    {carriedOverLimitPassed ? (
                        <Label
                            amount={policy.initialAmount + policy.initialBonusDays + policy.initialCarriedOverAmount - policy.carriedOverAmount}
                            label={t('totalDays')}
                        />
                    ) : (
                        <>
                            <Label amount={policy.amount} label={t('annualDays')} />
                            <Label amount={policy.carriedOverAmount} label={t('carriedOver')} />
                        </>
                    )}
                </>
            ) : (
                <Label amount={policy.initialAmount + policy.initialBonusDays} label={t('totalDays')} />
            )}
            <Label amount={usedAmountValue} label="Used Days" />
        </div>
    );
};
